import { useState, useEffect, useCallback } from 'react';
import { getPlayer, addTransaction, updateTransaction, deleteTransaction, createSchedule, updateSchedule, deleteSchedule, sendReminder, sendWelcome, shareWithEmails, updatePlayer, getProjects, notifyTransaction } from '../api';

const fmt = (n) => `$${Math.abs(n).toFixed(2)}`;

// Groups transactions that share a groupId (split payments) into a single display item.
// Returns a sorted array of display items — either individual transactions or group objects.
function buildDisplayItems(transactions) {
  const groups = {};
  const ungrouped = [];
  for (const t of transactions) {
    if (t.groupId) {
      if (!groups[t.groupId]) groups[t.groupId] = [];
      groups[t.groupId].push(t);
    } else {
      ungrouped.push(t);
    }
  }
  const groupedItems = Object.values(groups).map(txns => ({
    isGroup: true,
    groupId: txns[0].groupId,
    type: txns[0].type,
    date: txns[0].date,
    description: txns[0].description,
    totalAmount: txns.reduce((s, t) => s + t.amount, 0),
    transactions: txns.sort((a, b) => (b.scheduleId ? 1 : 0) - (a.scheduleId ? 1 : 0)),
    createdAt: txns[0].createdAt,
  }));
  return [...ungrouped, ...groupedItems].sort((a, b) => {
    const cmp = new Date(a.date) - new Date(b.date);
    return cmp !== 0 ? cmp : new Date(a.createdAt) - new Date(b.createdAt);
  });
}

export default function PlayerDetail({ playerId, onBack }) {
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [modal, setModal] = useState(null); // 'payment'|'credit'|'expense'|'adjustment'|'plan'|'remind'|'share'|'edit'
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await getPlayer(playerId);
      setPlayer(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [playerId]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { setModal(null); load(); };

  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  if (loading) return <div className="container"><p style={{ color: 'var(--muted)', padding: 40 }}>Loading…</p></div>;
  if (!player) return <div className="container"><div className="alert alert-error">Player not found.</div></div>;

  const balance = player.currentBalance;
  const transactions = player.transactions || [];

  const shareUrl = `${window.location.origin}/share/${player.shareToken}`;

  return (
    <div className="container">
      <div className="back-link" onClick={onBack}>← Back to Dashboard</div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Header */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: 24, fontWeight: 800 }}>{player.playerName}</h2>
            <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>
              {player.parentEmails?.join(', ') || 'No emails on file'}
            </div>
            {player.notes && <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 2 }}>{player.notes}</div>}
          </div>
        </div>

        {/* Key stats row */}
        {(() => {
          const totalOwed = (player.startingBalance || 0) + transactions.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0) - transactions.filter(t => t.type === 'adjustment' && t.amount < 0).reduce((s, t) => s + Math.abs(t.amount), 0);
          const totalScheduled = (player.paymentSchedules || []).reduce((s, sch) => s + sch.totalAmount, 0);
          const scheduleGap = totalOwed - totalScheduled;
          return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
              <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Total Owed</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--text)' }}>${totalOwed.toFixed(2)}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Starting + expenses</div>
              </div>
              <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Total Scheduled</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: totalScheduled >= totalOwed ? 'var(--green)' : 'var(--yellow)' }}>${totalScheduled.toFixed(2)}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                  {scheduleGap > 0 ? `$${scheduleGap.toFixed(2)} not yet scheduled` : scheduleGap < 0 ? `$${Math.abs(scheduleGap).toFixed(2)} over scheduled` : '✓ Fully scheduled'}
                </div>
              </div>
              <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Total Outstanding</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: balance > 0 ? 'var(--red)' : 'var(--green)' }}>
                  {balance > 0 ? `-$${balance.toFixed(2)}` : `✓ $${Math.abs(balance).toFixed(2)}`}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{balance > 0 ? 'Still owed' : 'Paid in full'}</div>
              </div>
            </div>
          );
        })()}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" onClick={() => setModal('payment')}>+ Payment</button>
          <button className="btn btn-green btn-sm" onClick={() => setModal('credit')}>+ Credit</button>
          <button className="btn btn-sm" style={{ background: '#fef3c7', color: '#92400e' }} onClick={() => setModal('expense')}>+ Expense</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setModal('adjustment')}>± Adjustment</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setModal('welcome')}>✉️ Welcome</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setModal('remind')}>📧 Remind</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setModal('share')}>🔗 Share</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setModal('edit')}>✏️ Edit</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {['overview', 'transactions', 'schedules'].map(t => (
          <div key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </div>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab player={player} transactions={transactions} shareUrl={shareUrl} />}
      {tab === 'transactions' && <TransactionsTab transactions={transactions} playerId={playerId} schedules={player.paymentSchedules || []} onDelete={refresh} onRefresh={refresh} />}
      {tab === 'schedules' && <SchedulesTab player={player} transactions={transactions} onRefresh={refresh} />}

      {/* Modals */}
      {(modal === 'payment' || modal === 'credit' || modal === 'expense' || modal === 'adjustment') && (
        <TransactionModal type={modal} playerId={playerId} schedules={player.paymentSchedules || []} onClose={() => setModal(null)} onSave={refresh} />
      )}
      {modal === 'welcome' && (
        <WelcomeModal player={player} onClose={() => setModal(null)} onSent={() => { setModal(null); showSuccess('Welcome letter sent!'); }} />
      )}
      {modal === 'remind' && (
        <ReminderModal player={player} onClose={() => setModal(null)} onSent={() => { setModal(null); showSuccess('Reminder sent!'); }} />
      )}
      {modal === 'share' && (
        <ShareModal player={player} shareUrl={shareUrl} onClose={() => setModal(null)} onSent={() => { setModal(null); showSuccess('Share link sent!'); }} />
      )}
      {modal === 'edit' && (
        <EditPlayerModal player={player} onClose={() => setModal(null)} onSave={refresh} />
      )}
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────

function OverviewTab({ player, transactions, shareUrl }) {
  const totalPaid = transactions.filter(t => t.type === 'payment').reduce((s, t) => s + t.amount, 0);
  const totalCredits = transactions.filter(t => t.type === 'credit').reduce((s, t) => s + t.amount, 0);
  const totalExpenses = transactions.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);

  return (
    <div>
      <div className="stat-row" style={{ marginBottom: 20 }}>
        <div className="stat-box">
          <div className="stat-label">Starting Balance</div>
          <div className="stat-value">${player.startingBalance?.toFixed(2)}</div>
        </div>
        <div className="stat-box">
          <div className="stat-label">Payments Made</div>
          <div className="stat-value" style={{ color: 'var(--green)' }}>${totalPaid.toFixed(2)}</div>
        </div>
        <div className="stat-box">
          <div className="stat-label">Credits Applied</div>
          <div className="stat-value" style={{ color: '#1e40af' }}>${totalCredits.toFixed(2)}</div>
        </div>
        <div className="stat-box">
          <div className="stat-label">Additional Expenses</div>
          <div className="stat-value" style={{ color: 'var(--red)' }}>${totalExpenses.toFixed(2)}</div>
        </div>
      </div>

      <div className="card">
        <div style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 8 }}>Parent share link</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input readOnly value={shareUrl} style={{ flex: 1, padding: '7px 10px', borderRadius: 6, border: '1.5px solid var(--border)', fontSize: 13 }} />
          <button className="btn btn-ghost btn-sm" onClick={() => { navigator.clipboard.writeText(shareUrl); }}>Copy</button>
        </div>
        <div className="helper-text" style={{ marginTop: 6 }}>Anyone with this link can view this player's balance. Use the Share button to email it.</div>
      </div>

      {transactions.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Recent Transactions</div>
          <div className="txn-list">
            <div className="txn-row header">
              <span>Date</span><span>Description</span><span>Type</span><span>Amount</span><span></span>
            </div>
            {buildDisplayItems([...transactions]).slice(-5).map(item => {
              if (item.isGroup) {
                return (
                  <div key={item.groupId} className="txn-row">
                    <span style={{ color: 'var(--muted)' }}>{item.date}</span>
                    <span>{item.description || '—'} <span style={{ fontSize: 11, color: 'var(--navy)', marginLeft: 6, background: '#e0e7ff', padding: '1px 5px', borderRadius: 3 }}>split ×{item.transactions.length}</span></span>
                    <span><span className={`badge badge-${item.type}`}>{item.type}</span></span>
                    <span className="txn-amount-positive">+{fmt(item.totalAmount)}</span>
                    <span></span>
                  </div>
                );
              }
              return <TxnRow key={item.transactionId} t={item} />;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Transactions Tab ─────────────────────────────────────────────────────────

function TransactionsTab({ transactions, playerId, schedules, onDelete, onRefresh }) {
  const [expanded, setExpanded] = useState(new Set());
  const [applyTarget, setApplyTarget] = useState(null);

  const items = buildDisplayItems([...transactions]);

  const toggleExpand = (groupId) => setExpanded(s => {
    const next = new Set(s);
    next.has(groupId) ? next.delete(groupId) : next.add(groupId);
    return next;
  });

  const handleDelete = async (txnId) => {
    if (window.confirm('Delete this transaction?')) {
      await deleteTransaction(playerId, txnId);
      onDelete();
    }
  };

  const scheduleName = (scheduleId) => schedules.find(s => s.scheduleId === scheduleId)?.title || 'Schedule';

  return (
    <div>
      {items.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>No transactions yet.</div>
      ) : (
        <div className="txn-list">
          <div className="txn-row header">
            <span>Date</span><span>Description</span><span>Type</span><span>Amount</span><span></span>
          </div>
          {items.map(item => {
            if (item.isGroup) {
              const isOpen = expanded.has(item.groupId);
              return (
                <div key={item.groupId}>
                  <div className="txn-row" style={{ cursor: 'pointer' }} onClick={() => toggleExpand(item.groupId)}>
                    <span style={{ color: 'var(--muted)' }}>{item.date}</span>
                    <span>{item.description || '—'} <span style={{ fontSize: 11, color: 'var(--navy)', marginLeft: 6, background: '#e0e7ff', padding: '1px 5px', borderRadius: 3 }}>split ×{item.transactions.length}</span></span>
                    <span><span className={`badge badge-${item.type}`}>{item.type}</span></span>
                    <span className="txn-amount-positive">+{fmt(item.totalAmount)}</span>
                    <span style={{ color: 'var(--muted)', fontSize: 11 }}>{isOpen ? '▲' : '▼'}</span>
                  </div>
                  {isOpen && item.transactions.map(t => (
                    <div key={t.transactionId} className="txn-row" style={{ background: 'var(--surface-2)' }}>
                      <span></span>
                      <span style={{ fontSize: 12, color: 'var(--muted)', paddingLeft: 16 }}>
                        {t.scheduleId ? `→ ${scheduleName(t.scheduleId)}` : '→ Unallocated'}
                      </span>
                      <span></span>
                      <span style={{ fontSize: 13 }}>{fmt(t.amount)}</span>
                      <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {!t.scheduleId && schedules.length > 0 && (
                          <button onClick={() => setApplyTarget(t)} style={{ fontSize: 11, color: 'var(--navy)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>Apply</button>
                        )}
                        <button onClick={() => handleDelete(t.transactionId)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 16 }}>×</button>
                      </span>
                    </div>
                  ))}
                </div>
              );
            }

            const isCredit = item.type === 'payment' || item.type === 'credit';
            const isUnallocated = isCredit && !item.scheduleId;
            return (
              <div key={item.transactionId} className="txn-row">
                <span style={{ color: 'var(--muted)' }}>{item.date}</span>
                <span>{item.description || '—'}{item.scheduleId && <span style={{ fontSize: 11, color: 'var(--navy)', marginLeft: 6, background: '#e0e7ff', padding: '1px 5px', borderRadius: 3 }}>{scheduleName(item.scheduleId)}</span>}</span>
                <span><span className={`badge badge-${item.type}`}>{item.type}</span></span>
                <span className={isCredit ? 'txn-amount-positive' : 'txn-amount-negative'}>
                  {isCredit ? '+' : '-'}{fmt(item.amount)}
                </span>
                <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {isUnallocated && schedules.length > 0 && (
                    <button onClick={() => setApplyTarget(item)} style={{ fontSize: 11, color: 'var(--navy)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>Apply</button>
                  )}
                  <button onClick={() => handleDelete(item.transactionId)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 16 }}>×</button>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {applyTarget && (
        <ApplyToScheduleModal
          transaction={applyTarget}
          schedules={schedules}
          playerId={playerId}
          onClose={() => setApplyTarget(null)}
          onSave={() => { setApplyTarget(null); onRefresh(); }}
        />
      )}
    </div>
  );
}

function TxnRow({ t, onDelete }) {
  const isCredit = t.type === 'payment' || t.type === 'credit';
  return (
    <div className="txn-row">
      <span style={{ color: 'var(--muted)' }}>{t.date}</span>
      <span>{t.description || '—'}{t.scheduleId && <span style={{ fontSize: 11, color: 'var(--navy)', marginLeft: 6, background: '#e0e7ff', padding: '1px 5px', borderRadius: 3 }}>Sched</span>}</span>
      <span><span className={`badge badge-${t.type}`}>{t.type}</span></span>
      <span className={isCredit ? 'txn-amount-positive' : 'txn-amount-negative'}>
        {isCredit ? '+' : '-'}{fmt(t.amount)}
      </span>
      <span>{onDelete && <button onClick={onDelete} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 16 }}>×</button>}</span>
    </div>
  );
}

// ─── Apply to Schedule Modal ──────────────────────────────────────────────────

function ApplyToScheduleModal({ transaction, schedules, playerId, onClose, onSave }) {
  const [scheduleId, setScheduleId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handle = async (e) => {
    e.preventDefault();
    if (!scheduleId) { setError('Please select a schedule'); return; }
    setLoading(true);
    try {
      await updateTransaction(playerId, transaction.transactionId, { scheduleId });
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>Apply to Schedule</h3>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
          Apply <strong>{fmt(transaction.amount)}</strong>{transaction.description ? ` (${transaction.description})` : ''} to a payment schedule.
        </p>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handle}>
          <div className="form-group">
            <label>Schedule</label>
            <select value={scheduleId} onChange={e => setScheduleId(e.target.value)} required autoFocus>
              <option value="">— Select schedule —</option>
              {schedules.map(s => <option key={s.scheduleId} value={s.scheduleId}>{s.title} (${s.totalAmount.toFixed(2)} total)</option>)}
            </select>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !scheduleId}>
              {loading ? 'Applying…' : 'Apply to Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Schedules Tab ────────────────────────────────────────────────────────────

function SchedulesTab({ player, transactions, onRefresh }) {
  const [editingSchedule, setEditingSchedule] = useState(null);
  const schedules = player.paymentSchedules || [];

  const handleDelete = async (scheduleId, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    await deleteSchedule(player.playerId, scheduleId);
    onRefresh();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 14, color: 'var(--muted)' }}>{schedules.length} schedule{schedules.length !== 1 ? 's' : ''}</div>
        <button className="btn btn-primary btn-sm" onClick={() => setEditingSchedule('new')}>+ Add Schedule</button>
      </div>

      {schedules.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <p style={{ color: 'var(--muted)', marginBottom: 16 }}>No payment schedules yet.</p>
          <button className="btn btn-primary" onClick={() => setEditingSchedule('new')}>Add First Schedule</button>
        </div>
      )}

      {[...schedules].sort((a, b) => {
        const aDate = a.schedule?.[0]?.dueDate || a.createdAt;
        const bDate = b.schedule?.[0]?.dueDate || b.createdAt;
        if (aDate !== bDate) return aDate.localeCompare(bDate);
        return (a.createdAt || '').localeCompare(b.createdAt || '');
      }).map(sched => {
        const schedPaid = transactions.filter(t => t.scheduleId === sched.scheduleId)
          .reduce((s, t) => {
            if (t.type === 'payment' || t.type === 'credit') return s + t.amount;
            if (t.type === 'adjustment') return s + t.amount; // negative = charge, positive = reduction
            return s;
          }, 0);
        const isSingle = sched.schedule.length === 1;
        const s0 = sched.schedule[0];
        const isFullyPaid = schedPaid >= sched.totalAmount;
        const nextUnpaid = sched.schedule.find((s, i) => schedPaid < sched.schedule.slice(0, i + 1).reduce((x, y) => x + y.amount, 0));
        const isOverdue = !isFullyPaid && nextUnpaid && new Date(nextUnpaid.dueDate) < new Date();

        return (
          <div key={sched.scheduleId} className="card" style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{sched.title}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                  {sched.schedule.length} payment{sched.schedule.length !== 1 ? 's' : ''} · Total: ${sched.totalAmount.toFixed(2)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditingSchedule(sched)}>Edit</button>
                <button className="btn btn-sm" style={{ background: '#fee2e2', color: '#991b1b' }} onClick={() => handleDelete(sched.scheduleId, sched.title)}>Delete</button>
              </div>
            </div>

            {isSingle ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: isFullyPaid ? 'var(--green)' : isOverdue ? 'var(--red)' : 'var(--navy)' }}>
                  ${s0.amount.toFixed(2)}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{s0.description}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>Due: {s0.dueDate}</div>
                  {!isFullyPaid && schedPaid > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--yellow)', marginTop: 2 }}>
                      ${schedPaid.toFixed(2)} paid · ${(sched.totalAmount - schedPaid).toFixed(2)} remaining
                    </div>
                  )}
                </div>
                <span className={`badge ${isFullyPaid ? 'badge-payment' : schedPaid > 0 ? 'badge-credit' : isOverdue ? 'badge-expense' : 'badge-credit'}`} style={{ marginLeft: 'auto' }}>
                  {isFullyPaid ? '✓ Paid' : schedPaid > 0 ? '⬤ Partial' : isOverdue ? '⚠ Overdue' : 'Upcoming'}
                </span>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                  <div><div style={{ fontSize: 11, color: 'var(--muted)' }}>Paid</div><div style={{ fontWeight: 700, color: 'var(--green)', fontSize: 15 }}>${schedPaid.toFixed(2)}</div></div>
                  <div><div style={{ fontSize: 11, color: 'var(--muted)' }}>Remaining</div><div style={{ fontWeight: 700, color: 'var(--red)', fontSize: 15 }}>${Math.max(0, sched.totalAmount - schedPaid).toFixed(2)}</div></div>
                </div>
                <div className="plan-grid">
                  {sched.schedule.map((s, i) => {
                    const cumulative = sched.schedule.slice(0, i + 1).reduce((sum, x) => sum + x.amount, 0);
                    const entryPaid = schedPaid >= cumulative;
                    const overdue = !entryPaid && new Date(s.dueDate) < new Date();
                    return (
                      <div key={i} className={`plan-month ${entryPaid ? 'paid' : overdue ? 'overdue' : 'due'}`}>
                        <div className="month-label" style={{ fontSize: 12 }}>{s.description}</div>
                        <div className="month-date">Due: {s.dueDate}</div>
                        <div className="month-amount">${s.amount.toFixed(2)}</div>
                        <div style={{ fontSize: 11, marginTop: 4 }}>{entryPaid ? '✓ Paid' : overdue ? '⚠ Overdue' : 'Upcoming'}</div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        );
      })}

      {editingSchedule !== null && (
        <ScheduleModal
          playerId={player.playerId}
          existing={editingSchedule === 'new' ? null : editingSchedule}
          onClose={() => setEditingSchedule(null)}
          onSave={() => { setEditingSchedule(null); onRefresh(); }}
        />
      )}
    </div>
  );
}

// ─── Placeholder so old reference compiles (unused) ──────────────────────────

function PaymentPlanTab({ player, transactions, onEdit }) {
  const plan = player.paymentPlan;
  if (!plan) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 40 }}>
        <p style={{ color: 'var(--muted)', marginBottom: 16 }}>No payment schedule set for this player.</p>
        <button className="btn btn-primary" onClick={onEdit}>Set Up Payment Schedule</button>
      </div>
    );
  }

  const planPayments = transactions.filter(t => t.appliedToPlan && t.type === 'payment');
  const paidTotal = planPayments.reduce((s, t) => s + t.amount, 0);
  const isSingle = plan.schedule.length === 1;
  const s0 = plan.schedule[0];
  const isPaid = paidTotal >= plan.totalAmount;
  const isOverdue = !isPaid && new Date(s0.dueDate) < new Date();

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isSingle ? 0 : 12 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              {plan.notes || (isSingle ? 'Payment Due' : 'Payment Schedule')}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onEdit}>Edit</button>
        </div>

        {isSingle ? (
          /* ── Single payment: clean one-liner layout ── */
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 14, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 32, fontWeight: 900, color: isPaid ? 'var(--green)' : isOverdue ? 'var(--red)' : 'var(--navy)' }}>
              ${s0.amount.toFixed(2)}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{s0.description}</div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>Due: {s0.dueDate}</div>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <span className={`badge ${isPaid ? 'badge-payment' : isOverdue ? 'badge-expense' : 'badge-credit'}`} style={{ fontSize: 13, padding: '4px 12px' }}>
                {isPaid ? '✓ Paid' : isOverdue ? '⚠ Overdue' : 'Upcoming'}
              </span>
            </div>
          </div>
        ) : (
          /* ── Multi-payment: summary stats + grid ── */
          <>
            <div style={{ display: 'flex', gap: 20 }}>
              <div><div style={{ fontSize: 12, color: 'var(--muted)' }}>Total Scheduled</div><div style={{ fontWeight: 700 }}>${plan.totalAmount.toFixed(2)}</div></div>
              <div><div style={{ fontSize: 12, color: 'var(--muted)' }}>Paid</div><div style={{ fontWeight: 700, color: 'var(--green)' }}>${paidTotal.toFixed(2)}</div></div>
              <div><div style={{ fontSize: 12, color: 'var(--muted)' }}>Remaining</div><div style={{ fontWeight: 700, color: 'var(--red)' }}>${Math.max(0, plan.totalAmount - paidTotal).toFixed(2)}</div></div>
            </div>
          </>
        )}
      </div>

      {!isSingle && (
        <div className="plan-grid">
          {plan.schedule.map((s, i) => {
            const cumulative = plan.schedule.slice(0, i + 1).reduce((sum, x) => sum + x.amount, 0);
            const entryPaid = paidTotal >= cumulative;
            const overdue = !entryPaid && new Date(s.dueDate) < new Date();
            return (
              <div key={i} className={`plan-month ${entryPaid ? 'paid' : overdue ? 'overdue' : 'due'}`}>
                <div className="month-label" style={{ fontSize: 13 }}>{s.description}</div>
                <div className="month-date">Due: {s.dueDate}</div>
                <div className="month-amount">${s.amount.toFixed(2)}</div>
                <div style={{ fontSize: 11, marginTop: 4 }}>{entryPaid ? '✓ Paid' : overdue ? '⚠ Overdue' : 'Upcoming'}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Transaction Modal ────────────────────────────────────────────────────────

function TransactionModal({ type, playerId, schedules, onClose, onSave }) {
  const [form, setForm] = useState({ amount: '', description: '', date: new Date().toISOString().split('T')[0], notifyParent: false });
  const [splitMode, setSplitMode] = useState(false);
  const [singleScheduleId, setSingleScheduleId] = useState(''); // for non-split single schedule
  const [allocations, setAllocations] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isPayment = type === 'payment' || type === 'credit';
  const canSplit = isPayment && schedules && schedules.length > 1;
  const total = parseFloat(form.amount) || 0;
  const allocated = Object.values(allocations).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const unallocated = Math.round((total - allocated) * 100) / 100;

  const titles = { payment: 'Record Payment', credit: 'Add Credit', expense: 'Add Expense', adjustment: 'Budget Adjustment' };
  const hints = {
    payment: 'A payment made by the family toward their balance.',
    credit: 'A credit from an external source (sponsorship, fundraiser, etc.).',
    expense: 'An additional charge to add to the family\'s balance.',
    adjustment: 'Manually adjust the balance. Use a negative amount to reduce the balance.',
  };

  const setAlloc = (scheduleId, val) => setAllocations(a => ({ ...a, [scheduleId]: val }));

  const handle = async (e) => {
    e.preventDefault();
    if (!total || total <= 0) { setError('Amount required'); return; }
    if (splitMode) {
      if (allocated <= 0) { setError('Enter at least one schedule allocation'); return; }
      if (allocated > total + 0.01) { setError(`Allocations ($${allocated.toFixed(2)}) exceed total ($${total.toFixed(2)})`); return; }
    }
    setError(''); setLoading(true);
    try {
      if (splitMode) {
        // All parts of a split share a groupId so they can be displayed together
        const groupId = Date.now().toString(36) + Math.random().toString(36).slice(2);
        const promises = [];
        for (const [scheduleId, val] of Object.entries(allocations)) {
          const amt = parseFloat(val);
          if (amt > 0) promises.push(addTransaction(playerId, { type, amount: amt, description: form.description, date: form.date, scheduleId, groupId }));
        }
        // Remainder goes to balance with no scheduleId — still tagged with groupId so user can apply it later
        if (unallocated > 0.005) promises.push(addTransaction(playerId, { type, amount: unallocated, description: form.description, date: form.date, scheduleId: null, groupId }));
        await Promise.all(promises);
      } else {
        await addTransaction(playerId, { ...form, type, amount: total, scheduleId: singleScheduleId || null });
      }
      if (form.notifyParent) await notifyTransaction(playerId, type, form.amount, form.description).catch(() => {});
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: splitMode ? 520 : 480 }} onClick={e => e.stopPropagation()}>
        <h3>{titles[type]}</h3>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>{hints[type]}</p>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handle}>
          <div className="form-row">
            <div className="form-group">
              <label>Total Amount ($)</label>
              <input type="number" step="0.01" min="0.01" value={form.amount} onChange={set('amount')} required autoFocus placeholder="0.00" />
            </div>
            <div className="form-group">
              <label>Date</label>
              <input type="date" value={form.date} onChange={set('date')} required />
            </div>
          </div>
          <div className="form-group">
            <label>Description</label>
            <input value={form.description} onChange={set('description')} placeholder="Optional note…" />
          </div>

          {/* Split across schedules */}
          {canSplit && (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 8 }}>
                <input type="checkbox" checked={splitMode} onChange={e => setSplitMode(e.target.checked)}
                  style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }} />
                <span>Split across multiple schedules</span>
              </label>
              {splitMode && total > 0 && (
                <div style={{ border: '1.5px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 8, padding: '8px 12px', background: 'var(--navy)', color: 'white', fontSize: 12, fontWeight: 700 }}>
                    <span>Schedule</span><span>Amount ($)</span>
                  </div>
                  {schedules.map(s => (
                    <div key={s.scheduleId} style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, color: 'var(--text)' }}>{s.title}</span>
                      <input type="number" step="0.01" min="0" max={total}
                        value={allocations[s.scheduleId] || ''}
                        onChange={e => setAlloc(s.scheduleId, e.target.value)}
                        placeholder="0.00"
                        style={{ padding: '5px 8px', border: '1.5px solid var(--border)', borderRadius: 6, fontSize: 13, color: 'var(--text)', background: 'var(--surface-2)' }} />
                    </div>
                  ))}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 8, padding: '8px 12px', background: 'var(--surface-2)', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, color: 'var(--muted)' }}>Unallocated (goes to balance)</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: unallocated < -0.005 ? 'var(--red)' : 'var(--text)', paddingLeft: 8 }}>
                      ${unallocated < 0 ? '−' : ''}{Math.abs(unallocated).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Single schedule dropdown — only when not in split mode */}
          {!splitMode && (type === 'payment' || type === 'credit' || type === 'adjustment') && schedules && schedules.length > 0 && (
            <div className="form-group">
              <label>Apply to Schedule (optional)</label>
              <select value={singleScheduleId} onChange={e => setSingleScheduleId(e.target.value)}>
                <option value="">— Just apply to overall balance —</option>
                {schedules.map(s => <option key={s.scheduleId} value={s.scheduleId}>{s.title}</option>)}
              </select>
              {type === 'adjustment' && <div className="helper-text">Use a negative amount to reduce what's owed on a schedule.</div>}
            </div>
          )}

          <div className="form-group" style={{ marginTop: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.notifyParent}
                onChange={e => setForm(f => ({ ...f, notifyParent: e.target.checked }))}
                style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }} />
              <span>Notify parent by email</span>
            </label>
            <div className="helper-text">Sends a reminder email with this transaction noted. Off by default.</div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Schedule Modal (create / edit) ──────────────────────────────────────────

const emptyRow = () => ({ description: '', dueDate: '', amount: '' });

function ScheduleModal({ playerId, existing, onClose, onSave }) {
  const [title, setTitle] = useState(existing?.title || '');
  const [rows, setRows] = useState(
    existing?.schedule?.map(s => ({ description: s.description, dueDate: s.dueDate, amount: String(s.amount) }))
    || [{ description: '', dueDate: '', amount: '' }]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const total = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const addRow = () => setRows(r => [...r, emptyRow()]);
  const removeRow = (i) => setRows(r => r.filter((_, idx) => idx !== i));
  const updateRow = (i, key, val) => setRows(r => r.map((row, idx) => idx === i ? { ...row, [key]: val } : row));

  const handle = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required.'); return; }
    if (rows.some(r => !r.description || !r.dueDate || !r.amount)) {
      setError('All rows need a description, due date, and amount.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const payload = { title, schedule: rows.map(r => ({ description: r.description, dueDate: r.dueDate, amount: parseFloat(r.amount) })) };
      if (existing) {
        await updateSchedule(playerId, existing.scheduleId, payload);
      } else {
        await createSchedule(playerId, payload);
      }
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Quick Setup state ──
  const [mode, setMode] = useState(existing ? 'manual' : 'quick');
  const [quick, setQuick] = useState({ totalAmount: '', months: '', startDate: new Date().toISOString().split('T')[0] });

  const generateRows = () => {
    const total = parseFloat(quick.totalAmount);
    const months = parseInt(quick.months);
    if (!total || !months || !quick.startDate) return;
    const base = Math.floor((total / months) * 100) / 100;
    const remainder = Math.round((total - base * months) * 100) / 100;
    const generated = Array.from({ length: months }, (_, i) => {
      const d = new Date(quick.startDate);
      d.setMonth(d.getMonth() + i);
      const amount = i === months - 1 ? (base + remainder).toFixed(2) : base.toFixed(2);
      return { description: `Payment ${i + 1}`, dueDate: d.toISOString().split('T')[0], amount };
    });
    setRows(generated);
    setMode('manual');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <h3>{existing ? 'Edit Schedule' : 'Add Payment Schedule'}</h3>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handle}>
          <div className="form-group">
            <label>Schedule Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} required autoFocus placeholder="e.g. Season Fees, Tournament #1, Deposit" />
          </div>

          {/* Mode toggle */}
          {!existing && (
            <div style={{ display: 'flex', gap: 0, border: '1.5px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
              {['quick', 'manual'].map(m => (
                <button key={m} type="button" onClick={() => setMode(m)}
                  style={{ flex: 1, padding: '8px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                    background: mode === m ? 'var(--navy)' : 'white', color: mode === m ? 'white' : 'var(--muted)' }}>
                  {m === 'quick' ? '⚡ Quick Setup' : '✏️ Manual Entry'}
                </button>
              ))}
            </div>
          )}

          {/* Quick Setup panel */}
          {mode === 'quick' && (
            <div style={{ background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
                Enter a total and number of months — payments will be split evenly, with any remainder added to the last payment.
              </div>
              <div className="form-row">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Total Amount ($)</label>
                  <input type="number" step="0.01" min="0.01" value={quick.totalAmount}
                    onChange={e => setQuick(q => ({ ...q, totalAmount: e.target.value }))} placeholder="e.g. 1500.00" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Number of Months</label>
                  <input type="number" min="1" max="24" value={quick.months}
                    onChange={e => setQuick(q => ({ ...q, months: e.target.value }))} placeholder="e.g. 6" />
                </div>
              </div>
              <div className="form-group" style={{ marginTop: 12, marginBottom: 12 }}>
                <label>First Payment Date</label>
                <input type="date" value={quick.startDate} onChange={e => setQuick(q => ({ ...q, startDate: e.target.value }))} />
              </div>
              {quick.totalAmount && quick.months && (() => {
                const base = Math.floor((parseFloat(quick.totalAmount) / parseInt(quick.months)) * 100) / 100;
                const remainder = Math.round((parseFloat(quick.totalAmount) - base * parseInt(quick.months)) * 100) / 100;
                return (
                  <div style={{ fontSize: 13, color: 'var(--navy)', marginBottom: 10 }}>
                    {parseInt(quick.months) - (remainder > 0 ? 1 : 0)} × ${base.toFixed(2)}
                    {remainder > 0 && ` + 1 × $${(base + remainder).toFixed(2)} (final)`}
                    {' '}= <strong>${parseFloat(quick.totalAmount).toFixed(2)}</strong>
                  </div>
                );
              })()}
              <button type="button" className="btn btn-primary btn-sm"
                onClick={generateRows} disabled={!quick.totalAmount || !quick.months || !quick.startDate}>
                Generate Payment Rows →
              </button>
            </div>
          )}

          {/* Manual rows table */}
          {mode === 'manual' && (
            <>
              <div style={{ border: '1.5px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px 110px 32px', gap: 8, padding: '8px 12px', background: 'var(--navy)', color: 'white', fontSize: 12, fontWeight: 700 }}>
                  <span>Description</span><span>Due Date</span><span>Amount ($)</span><span></span>
                </div>
                {rows.map((row, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 130px 110px 32px', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                    <input value={row.description} onChange={e => updateRow(i, 'description', e.target.value)}
                      placeholder="e.g. Payment 1" style={{ padding: '6px 8px', border: '1.5px solid var(--border)', borderRadius: 6, fontSize: 13, color: 'var(--text)', background: 'var(--surface-2)' }} />
                    <input type="date" value={row.dueDate} onChange={e => updateRow(i, 'dueDate', e.target.value)}
                      style={{ padding: '6px 8px', border: '1.5px solid var(--border)', borderRadius: 6, fontSize: 13, color: 'var(--text)', background: 'var(--surface-2)' }} />
                    <input type="number" step="0.01" min="0.01" value={row.amount} onChange={e => updateRow(i, 'amount', e.target.value)}
                      placeholder="0.00" style={{ padding: '6px 8px', border: '1.5px solid var(--border)', borderRadius: 6, fontSize: 13, color: 'var(--text)', background: 'var(--surface-2)' }} />
                    <button type="button" onClick={() => removeRow(i)} disabled={rows.length === 1}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 18, lineHeight: 1 }}>×</button>
                  </div>
                ))}
                <div style={{ padding: '8px 12px' }}>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={addRow}>+ Add Row</button>
                </div>
              </div>
              {total > 0 && (
                <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 14, color: 'var(--text)' }}>
                  Total: <strong>${total.toFixed(2)}</strong>
                </div>
              )}
            </>
          )}

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            {mode === 'manual' && (
              <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving…' : existing ? 'Save Changes' : 'Add Schedule'}</button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Welcome Modal ────────────────────────────────────────────────────────────

function WelcomeModal({ player, onClose, onSent }) {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handle = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await sendWelcome(player.playerId, message);
      onSent();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>✉️ Send Welcome Letter</h3>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
          Sends a welcome email to <strong>{player.parentEmails?.join(', ') || 'no emails on file'}</strong> with their balance, payment schedule, share link, and Venmo info.
        </p>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handle}>
          <div className="form-group">
            <label>Personal Note (optional)</label>
            <textarea rows={3} value={message} onChange={e => setMessage(e.target.value)}
              placeholder="e.g. Welcome to the team! Please reach out if you have any questions about the payment schedule." />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !player.parentEmails?.length}>
              {loading ? 'Sending…' : 'Send Welcome Letter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Reminder Modal ───────────────────────────────────────────────────────────

function ReminderModal({ player, onClose, onSent }) {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handle = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await sendReminder(player.playerId, message);
      onSent();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>Send Payment Reminder</h3>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
          Will be sent to: <strong>{player.parentEmails?.join(', ') || 'No emails on file'}</strong>
        </p>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handle}>
          <div className="form-group">
            <label>Custom Message (optional)</label>
            <textarea rows={3} value={message} onChange={e => setMessage(e.target.value)} placeholder="Add a personal note to the reminder…" />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !player.parentEmails?.length}>
              {loading ? 'Sending…' : 'Send Reminder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Share Modal ──────────────────────────────────────────────────────────────

function ShareModal({ player, shareUrl, onClose, onSent }) {
  const [emails, setEmails] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const emailList = emails.split(',').map(s => s.trim()).filter(Boolean);
      await shareWithEmails(player.playerId, emailList);
      onSent();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>Share Balance</h3>
        <div className="form-group">
          <label>Share Link</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input readOnly value={shareUrl} style={{ flex: 1 }} />
            <button type="button" className="btn btn-ghost btn-sm" onClick={copy}>{copied ? '✓ Copied' : 'Copy'}</button>
          </div>
          <div className="helper-text">Anyone with this link can view the balance. Link never expires.</div>
        </div>
        <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0', paddingTop: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>Or email the link</div>
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSend}>
            <div className="form-group">
              <label>Email Address(es)</label>
              <input value={emails} onChange={e => setEmails(e.target.value)} placeholder="person@email.com, other@email.com" />
              <div className="helper-text">Separate multiple with commas</div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>
              <button type="submit" className="btn btn-primary" disabled={loading || !emails.trim()}>
                {loading ? 'Sending…' : 'Send Link'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Edit Player Modal ────────────────────────────────────────────────────────

function EditPlayerModal({ player, onClose, onSave }) {
  const [form, setForm] = useState({
    playerName: player.playerName,
    parentEmails: (player.parentEmails || []).join(', '),
    startingBalance: player.startingBalance || '',
    notes: player.notes || '',
    projectId: player.projectId || '',
  });
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getProjects().then(setProjects).catch(() => {});
  }, []);

  const handle = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await updatePlayer(player.playerId, {
        ...form,
        parentEmails: form.parentEmails.split(',').map(s => s.trim()).filter(Boolean),
        startingBalance: parseFloat(form.startingBalance) || 0,
        projectId: form.projectId || null,
      });
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>Edit Player</h3>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handle}>
          <div className="form-group">
            <label>Player Name</label>
            <input value={form.playerName} onChange={set('playerName')} required />
          </div>
          <div className="form-group">
            <label>Starting Balance ($)</label>
            <input type="number" step="0.01" min="0" value={form.startingBalance} onChange={set('startingBalance')} />
            <div className="helper-text">Must be 0 or greater. To record a credit, use a Credit transaction instead.</div>
          </div>
          <div className="form-group">
            <label>Parent Email(s)</label>
            <input value={form.parentEmails} onChange={set('parentEmails')} placeholder="mom@email.com, dad@email.com" />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea value={form.notes} onChange={set('notes')} rows={2} />
          </div>
          {projects.length > 0 && (
            <div className="form-group">
              <label>Project</label>
              <select value={form.projectId} onChange={set('projectId')}>
                <option value="">— No project —</option>
                {projects.map(p => (
                  <option key={p.projectId} value={p.projectId}>
                    {p.name}{p.season ? ` (${p.season})` : ''}
                  </option>
                ))}
              </select>
              <div className="helper-text">Move this player to a different project.</div>
            </div>
          )}
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving…' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
